import Vue from 'vue'
import gql from 'graphql-tag'
import { Component, Watch } from 'vue-property-decorator'
import { State } from 'vuex-class'
import * as echarts from 'echarts'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { showErrors } from '@/utils/snack'
import { dateToDisplay, monthStartToUnix, monthEndToUnix } from '@/utils/dateTime'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'
import { statPoliciesByBook, statPoliciesByCarrier, statPoliciesByGA, statPoliciesBySource, statPoliciesByProduct, statAccountsByMonth, statPoliciesByType, statSourceEfficiency } from '@/modules/StatisticAnalysis/gql/statisticsAnalysis.gql'
import { ChartSeries } from '@/modules/StatisticAnalysis/interfaces/chartData'
import { Book } from '@/modules/Book/interfaces/book'
import { getBooks } from '@/modules/Book/gql/book.gql'
import dollarFilter from '@/utils/dollarFilter'
import { visitWithTypeInfo } from 'graphql'
import { PageState } from '../../../store/modules/page'
import AppFilterButton from '@/components/AppFilterButton.vue'
import AppSimpleDatePicker from '@/components/AppSimpleDatePicker.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'

@Component({
  components: {
    AppBreadcrumbs,
    AppDatePicker,
    AppSelector,
    AppListSearchBtn,
    AppSimpleDatePicker,
    AppFilterButton,
    AppSimpleSelector
  }
})
export default class StatisticAnalysis extends Vue {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Reports'
    },
    {
      text: 'Statistic Analysis'
    }
  ]
  searchVal = ''
  startDate = ''
  endDate = ''

  statisticsLoading = true
  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  dateToDisplay = dateToDisplay
  dollarFilter = dollarFilter

  policyByBookChartOption: any | null = null

  policyChartBookSeries: ChartSeries[] = []

  accountChartSeries: ChartSeries[] = []

  premiumChartBookSeries: ChartSeries[] = []

  books: Book[] = []

  targetBook = 0

  policyType = ['New Business', 'Renewal', 'Rewrite', 'Remarket', 'Cancellation', 'Endorsement', 'BOR']

  typeOptions = ['Carrier', 'Insurance Product', 'Source', 'GA']

  topOptions = ['5', '10']

  targetType = 0

  targetTypeTop = 0

  isMounted = false

  policyByBookData: any | null = null

  policyByTypeTotalData: any | null = null
  policyByTypeData: any | null = null

  policyByCarrierChartOption: any | null = null

  policyByCarrierSource: any[] = []

  accountData: any | null = null
  accountChartOption: any | null = null

  policyRateData: any | null = null
  policyRateChartOption: any | null = null

  targetPremiumBook = 0
  premiumByBookOption: any | null = null

  sourceData: any | null = null
  sourceChartOption: any | null = null

  get startDateStart () {
    return this.endDate ? this.$moment.unix(this.endDate).subtract(2, 'years').format('YYYY-MM') : '1900-01'
  }

  get startDateEnd () {
    return this.endDate ? this.$moment.unix(this.endDate).format('YYYY-MM') : '2999-01'
  }

  get endDateStart () {
    return this.endDate ? this.$moment.unix(this.startDate).format('YYYY-MM') : '1900-01'
  }

  get endDateEnd () {
    return this.endDate ? this.$moment.unix(this.startDate).add(2, 'years').format('YYYY-MM-') : '2999-01'
  }

  get bookNames () {
    const list = this.books.map(book => book.name)
    list.unshift('Total')
    list.push('Unknown')
    return list
  }

  get accountBookList () {
    const list = this.books.map(book => {
      return {
        id: +book.id,
        name: book.name
      }
    })
    const item = {
      id: 0,
      name: 'Unknown'
    }
    list.unshift(item)
    return list
  }

  @State(state => state.page) page!: PageState

  async mounted () {
    if (!this.startDate) {
      this.startDate = this.$moment.tz('America/New_York').subtract(1, 'years').startOf('month').unix()
    }
    if (!this.endDate) {
      this.endDate = this.$moment.tz('America/New_York').endOf('month').unix()
    }
    await this.getBooks()
    await this.getStatPoliciesByBook()
    this.accountSeriesInit()
    await this.getStatAccountsByMonth()
    await this.getStatPoliciesByCarrier()
    await this.getStatPoliciesByType()
    await this.getStatSourceEfficiency()
  }

  onResize () {
    if (this.isMounted) {
      this.policyByBookChartInit('resize')
      this.premiumByBookChartInit('resize')
      this.accountChartInit('resize')
      this.policyByCarrierChartInit('resize')
      this.policyRateChartInit('resize')
      this.sourceChartInit('resize')
    }
  }

  async getBooks () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getBooks,
        variables: {
          filter: {
            name: ''
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.books = result.data.getBooks.books
      this.policyByBookSeriesInit()
      this.premiumByBookSeriesInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  policyByBookSeriesInit () {
    const seriesList = []
    for (var m = 0; m < this.policyType.length; m++) {
      const seriesItem: ChartSeries = {}
      seriesItem.name = this.policyType[m]
      seriesItem.stack = 'book'
      seriesItem.type = 'bar'
      seriesItem.yAxisIndex = 0
      seriesItem.barMaxWidth = '48%'
      seriesItem.data = []
      seriesList.unshift(seriesItem)
    }
    const seriesCommissionItem: ChartSeries = {}
    seriesCommissionItem.name = 'Commission'
    seriesCommissionItem.type = 'line'
    seriesCommissionItem.yAxisIndex = 1
    seriesCommissionItem.smooth = true
    seriesCommissionItem.data = []
    seriesList.push(seriesCommissionItem)
    const seriesPremiumItem: ChartSeries = {}
    seriesPremiumItem.name = 'Premium'
    seriesPremiumItem.type = 'line'
    seriesPremiumItem.yAxisIndex = 2
    seriesPremiumItem.smooth = true
    seriesPremiumItem.data = []
    seriesList.push(seriesPremiumItem)
    this.policyChartBookSeries = seriesList
  }

  premiumByBookSeriesInit () {
    const seriesList = []
    const seriesPremiumItem: ChartSeries = {}
    seriesPremiumItem.name = 'Premium'
    seriesPremiumItem.type = 'line'
    seriesPremiumItem.yAxisIndex = 0
    seriesPremiumItem.smooth = true
    seriesPremiumItem.data = []
    seriesList.push(seriesPremiumItem)
    const seriesProjectedCommissionItem: ChartSeries = {}
    seriesProjectedCommissionItem.name = 'Projected Commission'
    seriesProjectedCommissionItem.type = 'line'
    seriesProjectedCommissionItem.yAxisIndex = 1
    seriesProjectedCommissionItem.smooth = true
    seriesProjectedCommissionItem.data = []
    seriesList.push(seriesProjectedCommissionItem)
    const seriesReceivedPremiumItem: ChartSeries = {}
    seriesReceivedPremiumItem.name = 'Received Commission'
    seriesReceivedPremiumItem.type = 'line'
    seriesReceivedPremiumItem.yAxisIndex = 2
    seriesReceivedPremiumItem.smooth = true
    seriesReceivedPremiumItem.data = []
    seriesList.push(seriesReceivedPremiumItem)
    this.premiumChartBookSeries = seriesList
  }

  accountSeriesInit () {
    const seriesList = []
    for (var m = 0; m < this.accountBookList.length; m++) {
      const seriesItem: ChartSeries = {}
      seriesItem.name = this.accountBookList[m].name
      seriesItem.stack = 'account'
      seriesItem.type = 'bar'
      seriesItem.barMaxWidth = '48%'
      seriesItem.data = []
      seriesList.unshift(seriesItem)
    }
    this.accountChartSeries = seriesList
  }

  async getStatPoliciesByBook () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''

    this.statisticsLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statPoliciesByBook,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policyByBookData = result.data.statPoliciesByBook
      this.isMounted = true
      this.statisticsLoading = false
      this.policyByBookDataInit()
      this.premiumByBookDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getStatPoliciesByType () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''

    this.statisticsLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statPoliciesByType,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policyRateData = result.data.statPoliciesByType
      this.policyRateDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getStatSourceEfficiency () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''

    this.statisticsLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statSourceEfficiency,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      let sourceData = this.$R.sortWith([this.$R.descend(this.$R.prop('proportion')), this.$R.ascend(this.$R.prop('sourceName'))])(result.data.statSourceEfficiency)
      if (sourceData.length > 60) {
        sourceData = sourceData.slice(0, 60)
      }
      this.sourceData = sourceData
      this.sourceDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }
  policyRateDataInit () {
    if (!this.policyRateData) {
      return
    }
    const xAxisData = []
    const seriesData = []
    const types = ['Cancel Rate', 'Renew Rate', 'Endorsment Rate']
    for (var m = 0; m < types.length; m++) {
      const item: ChartSeries = {
        name: types[m],
        type: 'line',
        smooth: true,
        data: []
      }
      seriesData.push(item)
    }
    for (var i = 0; i < this.policyRateData.length; i++) {
      const xAsisData = `${this.policyRateData[i].year}-${this.policyRateData[i].month}`
      const xAsisItem = this.$moment(xAsisData, 'YYYY-M').format('MMM YYYY')
      xAxisData.push(xAsisItem)
      if (this.policyRateData[i].all.length > 0) {
        const cancelRate = +(((this.policyRateData[i].cancelled.length / this.policyRateData[i].all.length) * 100).toFixed(2))
        const renewRate = +(((this.policyRateData[i].renew.length / this.policyRateData[i].all.length) * 100).toFixed(2))
        const endorsementRate = +(((this.policyRateData[i].endorsement.length / this.policyRateData[i].all.length) * 100).toFixed(2))
        seriesData[0].data!.push(cancelRate)
        seriesData[1].data!.push(renewRate)
        seriesData[2].data!.push(endorsementRate)
      } else {
        seriesData[0].data!.push(0)
        seriesData[1].data!.push(0)
        seriesData[2].data!.push(0)
      }
    }
    const colors = ['#516b91', '#59c4e6', '#93b7e3', '#a5e7f0']
    const option = {
      color: colors,
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          var res = '<div><p>' + params[0].name + '</p></div>'
          for (var i = 0; i < params.length; i++) {
            if (params[i].data !== undefined) {
              res +=
                `<p style="height: 24px;line-height: 24px; margin: 0;padding: 0;font-size: 14px;color: #333333;">${params[i].marker}${params[i].seriesName}: <span style="font-weight: bold;">${params[i].data}%</span></p>`
            }
          }
          return res
        }
      },
      legend: {},
      grid: {
        left: '1%',
        right: '3%',
        bottom: '0',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true
          },
          data: xAxisData
        }
      ],
      yAxis: {
        type: 'value'
      },
      series: seriesData
    }
    this.policyRateChartOption = option
    this.policyRateChartInit()
  }

  policyByBookDataInit () {
    if (!this.policyByBookData) {
      return
    }
    const xAxisData = []
    const seriesData = []
    for (var i = 0; i < this.policyByBookData.length; i++) {
      const xAsisData = `${this.policyByBookData[i].year}-${this.policyByBookData[i].month}`
      const xAsisItem = this.$moment(xAsisData, 'YYYY-M').format('MMM YYYY')
      xAxisData.push(xAsisItem)
      var allBusiness = 0
      var allRenewal = 0
      var allRewrite = 0
      var allRemarket = 0
      var allCancelled = 0
      var allEndorsement = 0
      var allBor = 0
      var allCommission = 0
      var allPremium = 0
      for (var m = 0; m < this.policyByBookData[i].stats.length; m++) {
        allBusiness += this.policyByBookData[i].stats[m].newBusinessIds.length
        allRenewal += this.policyByBookData[i].stats[m].renewalIds.length
        allRewrite += this.policyByBookData[i].stats[m].rewriteIds.length
        allRemarket += this.policyByBookData[i].stats[m].remarketIds.length
        allCancelled += this.policyByBookData[i].stats[m].cancelledIds.length
        allEndorsement += this.policyByBookData[i].stats[m].endorsementIds.length
        allBor += this.policyByBookData[i].stats[m].borIds.length
        allCommission += (+this.policyByBookData[i].stats[m].totalCommission)
        allPremium += (+this.policyByBookData[i].stats[m].totalPremium)
        if ((this.targetBook > 0) && this.policyByBookData[i].stats[m].bookName === this.bookNames[this.targetBook]) {
          this.policyChartBookSeries[0].data!.push(this.policyByBookData[i].stats[m].bookId.length)
          this.policyChartBookSeries[1].data!.push(this.policyByBookData[i].stats[m].endorsementIds.length)
          this.policyChartBookSeries[2].data!.push(this.policyByBookData[i].stats[m].cancelledIds.length)
          this.policyChartBookSeries[3].data!.push(this.policyByBookData[i].stats[m].remarketIds.length)
          this.policyChartBookSeries[4].data!.push(this.policyByBookData[i].stats[m].rewriteIds.length)
          this.policyChartBookSeries[5].data!.push(this.policyByBookData[i].stats[m].renewalIds.length)
          this.policyChartBookSeries[6].data!.push(this.policyByBookData[i].stats[m].newBusinessIds.length)
          this.policyChartBookSeries[7].data!.push(this.policyByBookData[i].stats[m].totalCommission.toFixed(2))
          this.policyChartBookSeries[8].data!.push(this.policyByBookData[i].stats[m].totalPremium.toFixed(2))
        }
      }
      if (this.targetBook === 0) {
        this.policyChartBookSeries[0].data!.push(allBor)
        this.policyChartBookSeries[1].data!.push(allEndorsement)
        this.policyChartBookSeries[2].data!.push(allCancelled)
        this.policyChartBookSeries[3].data!.push(allRemarket)
        this.policyChartBookSeries[4].data!.push(allRewrite)
        this.policyChartBookSeries[5].data!.push(allRenewal)
        this.policyChartBookSeries[6].data!.push(allBusiness)
        this.policyChartBookSeries[7].data!.push(+allCommission.toFixed(2))
        this.policyChartBookSeries[8].data!.push(+allPremium.toFixed(2))
      }
    }
    const colors = ['#516b91', '#59c4e6', '#edafda', '#a5e7f0', '#cbb0e3', '#93b7e3', '#b084d7', '#339ca8', '#cda819']
    const option = {
      color: colors,
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          var res = '<div><p>' + params[0].name + '</p></div>'
          let total = 0
          for (var i = 0; i < params.length; i++) {
            if (params[i].data !== undefined) {
              if (i < 7) {
                total += params[i].data
              }
              res +=
                `<p style="height: 24px;line-height: 24px; margin: 0;padding: 0;font-size: 14px;color: #333333;">${params[i].marker}${params[i].seriesName}: <span style="font-weight: bold;">${(params[i].seriesName === 'Premium' || params[i].seriesName === 'Commission') ? dollarFilter(params[i].data) : params[i].data}</span></p>`
              if (i === 6) {
                res +=
                `<p style="height: 24px;line-height: 24px; margin: 0;padding: 0;font-size: 14px;color: #333333;">Policy Total Count: <span style="font-weight: bold;">${total}</span></p>`
              }
            }
          }
          return res
        }
      },
      legend: {},
      grid: {
        left: '1%',
        right: '3%',
        bottom: '0',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true
          },
          data: xAxisData
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: 'Number',
          position: 'left',
          axisLine: {
            show: true,
            lineStyle: {
              color: colors[0]
            }
          }
        },
        {
          type: 'value',
          name: 'Commission',
          position: 'right',
          offset: 80,
          axisLine: {
            show: true,
            lineStyle: {
              color: colors[7]
            }
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            formatter: '$ {value}'
          }
        },
        {
          type: 'value',
          name: 'Premium',
          position: 'right',
          axisLine: {
            show: true,
            lineStyle: {
              color: colors[8]
            }
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            formatter: '$ {value}'
          }
        }
      ],
      series: this.policyChartBookSeries
    }
    this.policyByBookChartOption = option
    this.policyByBookChartInit()
  }

  premiumByBookDataInit () {
    if (!this.policyByBookData) {
      return
    }
    const xAxisData = []
    const seriesData = []
    for (var i = 0; i < this.policyByBookData.length; i++) {
      const xAsisData = `${this.policyByBookData[i].year}-${this.policyByBookData[i].month}`
      const xAsisItem = this.$moment(xAsisData, 'YYYY-M').format('MMM YYYY')
      xAxisData.push(xAsisItem)
      var allPremium = 0
      var allProjectedCommission = 0
      var allReceivedCommission = 0
      for (var m = 0; m < this.policyByBookData[i].stats.length; m++) {
        allPremium += (+this.policyByBookData[i].stats[m].totalPremium)
        allProjectedCommission += (+this.policyByBookData[i].stats[m].totalCommission)
        allReceivedCommission += (+this.policyByBookData[i].stats[m].totalCommissionReceived)
        if ((this.targetPremiumBook > 0) && this.policyByBookData[i].stats[m].bookName === this.bookNames[this.targetPremiumBook]) {
          this.premiumChartBookSeries[0].data!.push(this.policyByBookData[i].stats[m].totalPremium.toFixed(2))
          this.premiumChartBookSeries[1].data!.push(this.policyByBookData[i].stats[m].totalCommission.toFixed(2))
          this.premiumChartBookSeries[2].data!.push(this.policyByBookData[i].stats[m].totalCommissionReceived.toFixed(2))
        }
      }
      if (this.targetPremiumBook === 0) {
        this.premiumChartBookSeries[0].data!.push(+allPremium.toFixed(2))
        this.premiumChartBookSeries[1].data!.push(+allProjectedCommission.toFixed(2))
        this.premiumChartBookSeries[2].data!.push(+allReceivedCommission.toFixed(2))
      }
    }
    const colors = ['#516b91', '#59c4e6', '#93b7e3']
    const option = {
      color: colors,
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          var res = '<div><p>' + params[0].name + '</p></div>'
          for (var i = 0; i < params.length; i++) {
            if (params[i].data !== undefined) {
              res +=
                `<p style="height: 24px;line-height: 24px; margin: 0;padding: 0;font-size: 14px;color: #333333;">${params[i].marker}${params[i].seriesName}: <span style="font-weight: bold;">${dollarFilter(params[i].data)}</span></p>`
            }
          }
          return res
        }
      },
      legend: {},
      grid: {
        left: '1%',
        right: '7%',
        bottom: '0',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true
          },
          data: xAxisData
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: 'Premium',
          position: 'left',
          axisLine: {
            show: true,
            lineStyle: {
              color: colors[0]
            }
          }
        },
        {
          type: 'value',
          name: 'Projected Commission',
          position: 'right',
          axisLine: {
            show: true,
            lineStyle: {
              color: colors[1]
            }
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            formatter: '$ {value}'
          }
        },
        {
          type: 'value',
          name: 'Received Commission',
          position: 'right',
          offset: 130,
          axisLine: {
            show: true,
            lineStyle: {
              color: colors[2]
            }
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            formatter: '$ {value}'
          }
        }
      ],
      series: this.premiumChartBookSeries
    }
    this.premiumByBookOption = option
    this.premiumByBookChartInit()
  }

  accountDataInit () {
    if (!this.accountData) {
      return
    }
    const xAxisData = []
    const seriesData = []
    for (var i = 0; i < this.accountData.length; i++) {
      const xAsisData = `${this.accountData[i].year}-${this.accountData[i].month}`
      const xAsisItem = this.$moment(xAsisData, 'YYYY-M').format('MMM YYYY')
      xAxisData.push(xAsisItem)
      const accountItem: any = new Map()
      for (var m = 0; m < this.accountData[i].stats.length; m++) {
        accountItem[this.accountData[i].stats[m].bookName] = this.accountData[i].stats[m].totalCount
      }
      for (var n = 0; n < this.accountChartSeries.length; n++) {
        this.accountChartSeries[n].data!.push(accountItem[this.accountChartSeries[n].name!])
      }
    }
    const colors = ['#516b91', '#59c4e6', '#93b7e3', '#a5e7f0', '#b084d7', '#339ca8', '#cda819']
    const option = {
      color: colors,
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any) {
          var res = '<div><p>' + params[0].name + '</p></div>'
          let total = 0
          for (var i = 0; i < params.length; i++) {
            if (params[i].data !== undefined) {
              total += params[i].data
              res +=
                `<p style="height: 24px;line-height: 24px; margin: 0;padding: 0;font-size: 14px;color: #333333;">${params[i].marker}${params[i].seriesName}: <span style="font-weight: bold;">${(params[i].seriesName === 'Premium' || params[i].seriesName === 'Commission') ? '$' : ''}${params[i].data}</span></p>`
              if (i === params.length - 1) {
                res +=
                `<p style="height: 24px;line-height: 24px; margin: 0;padding: 0;font-size: 14px;color: #333333;">Account Total Count: <span style="font-weight: bold;">${total}</span></p>`
              }
            }
          }
          return res
        }
      },
      legend: {},
      grid: {
        left: '1%',
        right: '1%',
        bottom: '0',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true
          },
          data: xAxisData
        }
      ],
      yAxis: {
        type: 'value'
      },
      series: this.accountChartSeries
    }
    this.accountChartOption = option
    this.accountChartInit()
  }

  sourceDataInit () {
    if (!this.sourceData) {
      return
    }
    const xAxisData = []
    const seriesData = []
    for (var i = 0; i < this.sourceData.length; i++) {
      xAxisData.push(this.sourceData[i].sourceName)
      seriesData.push(+this.sourceData[i].proportion * 100)
    }
    const colors = ['#516b91', '#59c4e6', '#93b7e3', '#a5e7f0', '#b084d7', '#339ca8', '#cda819']
    const option = {
      color: colors,
      legend: {},
      grid: {
        left: '1%',
        right: '1%',
        bottom: '0',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          axisLabel: {
            interval: 0,
            rotate: '45'
          },
          data: xAxisData
        }
      ],
      yAxis: {
        type: 'value'
      },
      series: [
        {
          data: seriesData,
          type: 'bar',
          barWidth: '20%'
        }
      ]
    }
    this.sourceChartOption = option
    this.sourceChartInit()
  }

  policyByTypeDataInit () {
    if (!this.policyByTypeTotalData) {
      return
    }
    if (this.targetTypeTop === 0) {
      const list = JSON.parse(JSON.stringify(this.policyByTypeTotalData))
      this.$R.forEach((item: any) => {
        item.stats = item.stats.slice(0, 5)
      }, list)
      this.policyByTypeData = list.concat()
    } else if (this.targetTypeTop === 1) {
      this.policyByTypeData = JSON.parse(JSON.stringify(this.policyByTypeTotalData))
    }
    const source = []
    const series = []
    for (var n = 0; n < this.policyByTypeData[0].stats.length; n++) {
      const sourceItem = []
      sourceItem.push(this.policyByTypeData[0].stats[n].name)
      source.push(sourceItem)
      const seriedItem = {
        type: 'line',
        smooth: true,
        seriesLayoutBy: 'row',
        emphasis: { focus: 'series' }
      }
      series.push(seriedItem)
    }
    const sourceZero = []
    sourceZero.push('policy')
    source.unshift(sourceZero)
    for (var i = 0; i < this.policyByTypeData.length; i++) {
      const xAsisData = `${this.policyByTypeData[i].year}-${this.policyByTypeData[i].month}`
      const xAsisItem = this.$moment(xAsisData, 'YYYY-M').format('MMM YYYY')
      source[0].push(xAsisItem)
      for (var m = 0; m < this.policyByTypeData[i].stats.length; m++) {
        const sourceIndex = m + 1
        source[sourceIndex].push(this.policyByTypeData[i].stats[m].policyIds.length)
      }
    }
    const seriesLast = {
      type: 'pie',
      id: 'pie',
      radius: '30%',
      center: ['50%', '25%'],
      top: 20,
      emphasis: {
        focus: 'self'
      },
      label: {
        formatter: '{b}: {@[1]} ({d}%)'
      },
      encode: {
        itemName: 'policy',
        value: source[0][1],
        tooltip: source[0][1]
      }
    }
    series.push(seriesLast)
    const colors = ['#516b91', '#59c4e6', '#edafda', '#a5e7f0', '#cbb0e3', '#93b7e3', '#b084d7', '#339ca8', '#cda819', '#dc6e6a', '#5c9f76']
    const option = {
      color: colors,
      legend: {},
      tooltip: {
        trigger: 'axis',
        showContent: false
      },
      dataset: {
        source
      },
      xAxis: { type: 'category' },
      yAxis: { gridIndex: 0 },
      grid: {
        top: '45%',
        bottom: '5%',
        left: '3%',
        right: '3%'
      },
      series
    }
    this.policyByCarrierChartOption = option
    this.policyByCarrierChartInit()
  }

  policyByBookChartInit (type?: string) {
    const newBusinessPolicyChartDom = (this.$refs[`newBusinessPolicy${this.targetBook}`] as any)[0] as HTMLCanvasElement
    if (!newBusinessPolicyChartDom) {
      return
    }
    const newBusinessPolicyeChart = echarts.init(newBusinessPolicyChartDom)
    const option = this.policyByBookChartOption
    if (type === 'resize') {
      newBusinessPolicyeChart.resize()
    } else {
      newBusinessPolicyeChart.setOption(option)
    }
  }

  premiumByBookChartInit (type?: string) {
    const premiumChartDom = (this.$refs[`premiumChart${this.targetPremiumBook}`] as any)[0] as HTMLCanvasElement
    if (!premiumChartDom) {
      return
    }
    const premiumChart = echarts.init(premiumChartDom)
    const option = this.premiumByBookOption
    if (type === 'resize') {
      premiumChart.resize()
    } else {
      premiumChart.setOption(option)
    }
  }

  accountChartInit (type?: string) {
    const accountChartDom = this.$refs.accountChart as HTMLCanvasElement
    if (!accountChartDom) {
      return
    }
    const accountChart = echarts.init(accountChartDom)
    const option = this.accountChartOption
    if (type === 'resize') {
      accountChart.resize()
    } else {
      accountChart.setOption(option)
    }
  }

  sourceChartInit (type?: string) {
    const sourceChartDom = this.$refs.sourceChart as HTMLCanvasElement
    if (!sourceChartDom) {
      return
    }
    const sourceChart = echarts.init(sourceChartDom)
    const option = this.sourceChartOption
    if (type === 'resize') {
      sourceChart.resize()
    } else {
      sourceChart.setOption(option)
    }
  }

  policyByCarrierChartInit (type?: string) {
    const policyByCarrierChartDom = (this.$refs[`typeTargetPolicy${this.targetType}`] as any)[0] as HTMLCanvasElement
    if (!policyByCarrierChartDom) {
      return
    }
    const policyByCarrierChart = echarts.init(policyByCarrierChartDom)
    policyByCarrierChart.on('updateAxisPointer', function (event: any) {
      const xAxisInfo = event.axesInfo[0]
      if (xAxisInfo) {
        const dimension = xAxisInfo.value + 1
        policyByCarrierChart.setOption({
          series: {
            id: 'pie',
            label: {
              formatter: '{b}: {@[' + dimension + ']} ({d}%)'
            },
            encode: {
              value: dimension,
              tooltip: dimension
            }
          }
        })
      }
    })
    const option = this.policyByCarrierChartOption
    if (type === 'resize') {
      policyByCarrierChart.resize()
    } else {
      policyByCarrierChart.setOption(option)
    }
  }

  policyRateChartInit (type?: string) {
    const policyRateChartDom = this.$refs.policyRateChart as HTMLCanvasElement
    if (!policyRateChartDom) {
      return
    }
    const policyRateChart = echarts.init(policyRateChartDom)
    const option = this.policyRateChartOption
    if (type === 'resize') {
      policyRateChart.resize()
    } else {
      policyRateChart.setOption(option)
    }
  }

  async getStatAccountsByMonth () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statAccountsByMonth,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.accountData = result.data.statAccountsByMonth
      this.accountDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getStatPoliciesByCarrier () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statPoliciesByCarrier,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policyByTypeTotalData = result.data.statPoliciesByCarrier
      this.policyByTypeDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getStatPoliciesByProduct () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statPoliciesByProduct,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policyByTypeTotalData = result.data.statPoliciesByProduct
      this.policyByTypeDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getStatPoliciesBySource () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statPoliciesBySource,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policyByTypeTotalData = result.data.statPoliciesBySource
      this.policyByTypeDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getStatPoliciesByGA () {
    if (!this.startDate || !this.endDate) {
      return
    }
    const startDate = monthStartToUnix(+this.startDate) + ''
    const endDate = monthEndToUnix(+this.endDate) + ''
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: statPoliciesByGA,
        variables: {
          startDate,
          endDate
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policyByTypeTotalData = result.data.statPoliciesByGA
      this.policyByTypeDataInit()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  changeBookHandler () {
    this.policyByBookSeriesInit()
    this.policyByBookDataInit()
  }

  changePremiumBookHandler () {
    this.premiumByBookSeriesInit()
    this.premiumByBookDataInit()
  }

  changeTypeHandler () {
    switch (this.targetType) {
      case 0:
        this.getStatPoliciesByCarrier()
        break
      case 1:
        this.getStatPoliciesByProduct()
        break
      case 2:
        this.getStatPoliciesBySource()
        break
      case 3:
        this.getStatPoliciesByGA()
        break
      default:
        break
    }
  }

  changeTopHandler () {
    this.policyByTypeDataInit()
  }

  async changeMonthHandler () {
    this.policyByBookSeriesInit()
    this.premiumByBookSeriesInit()
    await this.getStatPoliciesByBook()
    this.accountSeriesInit()
    await this.getStatAccountsByMonth()
    this.changeTypeHandler()
    await this.getStatPoliciesByType()
    await this.getStatSourceEfficiency()
  }
}
